import { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Stack,
  type SxProps,
  type Theme,
  ThemeProvider,
  Typography,
  alpha,
  keyframes,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import type { PostHogErrorBoundaryFallbackProps } from 'posthog-js/react';
import { lightTheme } from '@/shared/ui/theme';
import { Logo } from '@/shared/ui/Logo';

const BRAND_RED = '#CA0123';
const INK = '#1C1B1F';
const PAPER = '#FFFBFE';
const MONO = '"JetBrains Mono", "SFMono-Regular", "Menlo", "Consolas", "Courier New", monospace';

const rise = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const blink = keyframes`
  0%, 49%   { opacity: 1; }
  50%, 100% { opacity: 0; }
`;
const sweep = keyframes`
  0%       { transform: rotate(-80deg); }
  45%, 55% { transform: rotate(72deg); }
  100%     { transform: rotate(-80deg); }
`;
const scan = keyframes`
  0%       { transform: translateY(-100%); opacity: 0; }
  20%, 80% { opacity: 0.35; }
  100%     { transform: translateY(100%); opacity: 0; }
`;

const riseIn = (delay = 0) => ({
  opacity: 0,
  animation: `${rise} 500ms ease-out ${delay}ms forwards`,
});

const styles = {
  root: {
    position: 'fixed',
    inset: 0,
    overflow: 'auto',
    bgcolor: PAPER,
    color: INK,
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    backgroundImage: `
      radial-gradient(ellipse 60% 40% at 50% 38%, ${alpha(BRAND_RED, 0.08)} 0%, transparent 70%),
      linear-gradient(${alpha(INK, 0.04)} 1px, transparent 1px),
      linear-gradient(90deg, ${alpha(INK, 0.04)} 1px, transparent 1px)
    `,
    backgroundSize: '100% 100%, 32px 32px, 32px 32px',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      background: `linear-gradient(180deg, transparent 0%, ${alpha(BRAND_RED, 0.15)} 50%, transparent 100%)`,
      mixBlendMode: 'multiply',
      animation: `${scan} 7s ease-in-out infinite`,
      pointerEvents: 'none',
    },
  },
  container: {
    position: 'relative',
    zIndex: 1,
    maxWidth: 720,
    mx: 'auto',
    px: { xs: 3, sm: 6 },
    py: { xs: 6, sm: 10 },
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    mb: { xs: 6, sm: 10 },
  },
  faultBadge: {
    alignItems: 'center',
    fontFamily: MONO,
    fontSize: 12,
    color: alpha(INK, 0.6),
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    bgcolor: BRAND_RED,
    boxShadow: `0 0 12px ${BRAND_RED}`,
    animation: `${blink} 1.1s steps(2, start) infinite`,
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  codeLabel: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 2,
    mb: 2,
    fontFamily: MONO,
    fontSize: 12,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: alpha(INK, 0.5),
  },
  codeDivider: {
    flex: 1,
    height: '1px',
    background: `repeating-linear-gradient(90deg, ${alpha(INK, 0.3)} 0 4px, transparent 4px 8px)`,
  },
  codeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: 2, sm: 4 },
    mb: 4,
  },
  codeText: {
    fontFamily: MONO,
    fontWeight: 700,
    fontSize: { xs: '4rem', sm: '6.5rem', md: '8rem' },
    lineHeight: 0.9,
    letterSpacing: '-0.04em',
    color: INK,
    textShadow: `4px 4px 0 ${alpha(BRAND_RED, 0.15)}`,
  },
  heading: {
    fontSize: { xs: '1.75rem', sm: '2.5rem' },
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.1,
    mb: 2,
  },
  body: {
    fontSize: '1.0625rem',
    lineHeight: 1.55,
    color: alpha(INK, 0.7),
    maxWidth: 520,
    mb: 5,
  },
  primaryBtn: {
    py: 1.5,
    px: 4,
    fontSize: '0.9375rem',
    fontWeight: 600,
  },
  secondaryBtn: {
    py: 1.5,
    px: 4,
    fontSize: '0.9375rem',
    fontWeight: 600,
    borderColor: alpha(INK, 0.2),
    color: INK,
    '&:hover': {
      borderColor: INK,
      bgcolor: alpha(INK, 0.04),
    },
  },
  detailsBtn: {
    fontFamily: MONO,
    fontSize: 12,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: alpha(INK, 0.6),
    px: 0,
    '&:hover': { bgcolor: 'transparent', color: INK },
  },
  detailsPanel: {
    mt: 2,
    p: 3,
    border: `1px solid ${alpha(INK, 0.12)}`,
    borderLeft: `3px solid ${BRAND_RED}`,
    borderRadius: '8px',
    bgcolor: alpha(INK, 0.02),
    fontFamily: MONO,
    fontSize: 12,
    lineHeight: 1.6,
    color: alpha(INK, 0.85),
    maxHeight: 280,
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  footer: {
    mt: 8,
    pt: 3,
    borderTop: `1px dashed ${alpha(INK, 0.15)}`,
    display: 'flex',
    justifyContent: 'space-between',
    fontFamily: MONO,
    fontSize: 11,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: alpha(INK, 0.4),
  },
  gauge: {
    position: 'relative',
    width: { xs: 80, sm: 120 },
    height: { xs: 80, sm: 120 },
    flexShrink: 0,
  },
  gaugeSvg: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
  },
  gaugeNeedle: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '3px',
    height: '36%',
    bgcolor: BRAND_RED,
    transformOrigin: '50% 100%',
    transform: 'translate(-50%, -100%) rotate(72deg)',
    borderRadius: '2px 2px 0 0',
    animation: `${sweep} 3.5s cubic-bezier(0.65, 0, 0.35, 1) infinite`,
    boxShadow: `0 0 10px ${alpha(BRAND_RED, 0.6)}`,
  },
  gaugeHub: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 14,
    height: 14,
    bgcolor: INK,
    borderRadius: '50%',
    transform: 'translate(-50%, -50%)',
    border: `3px solid ${PAPER}`,
    boxShadow: `0 0 0 1px ${alpha(INK, 0.15)}`,
  },
} satisfies Record<string, SxProps<Theme>>;

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return 'Unknown fault';
};

const getTroubleCode = (error: unknown): string => {
  const source = getErrorMessage(error);
  let hash = 0;
  for (let i = 0; i < source.length; i += 1) {
    hash = (hash * 31 + source.charCodeAt(i)) | 0;
  }
  return `P${Math.abs(hash % 10000)
    .toString()
    .padStart(4, '0')}`;
};

const handleReload = () => window.location.reload();
const handleHome = () => window.location.assign('/');

const TICK_ANGLES = [-90, -45, 0, 45, 90];

const Gauge = () => (
  <Box sx={styles.gauge}>
    <Box component="svg" viewBox="0 0 100 100" sx={styles.gaugeSvg}>
      <circle cx="50" cy="50" r="47" fill="none" stroke={alpha(INK, 0.12)} strokeWidth="2" />
      <path
        d="M 10 50 A 40 40 0 0 1 90 50"
        fill="none"
        stroke={alpha(INK, 0.15)}
        strokeWidth="4"
        strokeLinecap="round"
      />
      <path d="M 70 15.36 A 40 40 0 0 1 90 50" fill="none" stroke={BRAND_RED} strokeWidth="4" strokeLinecap="round" />
      {TICK_ANGLES.map((deg) => {
        const rad = (deg * Math.PI) / 180;
        const sin = Math.sin(rad);
        const cos = Math.cos(rad);
        return (
          <line
            key={deg}
            x1={50 + sin * 44}
            y1={50 - cos * 44}
            x2={50 + sin * 38}
            y2={50 - cos * 38}
            stroke={deg >= 45 ? BRAND_RED : alpha(INK, 0.35)}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        );
      })}
    </Box>
    <Box sx={styles.gaugeNeedle} />
    <Box sx={styles.gaugeHub} />
  </Box>
);

type TechnicalDetailsProps = {
  troubleCode: string;
  errorMessage: string;
  componentStack: string;
};

const TechnicalDetails = ({ troubleCode, errorMessage, componentStack }: TechnicalDetailsProps) => {
  const [open, setOpen] = useState(false);
  const handleToggle = () => setOpen((v) => !v);
  return (
    <Box sx={riseIn(560)}>
      <Button
        onClick={handleToggle}
        startIcon={<BuildRoundedIcon sx={{ fontSize: 18 }} />}
        endIcon={
          <ExpandMoreRoundedIcon
            sx={{
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 200ms ease',
            }}
          />
        }
        sx={styles.detailsBtn}
      >
        Technical details
      </Button>
      <Collapse in={open}>
        <Box sx={styles.detailsPanel}>
          <Box sx={{ color: BRAND_RED, mb: 1, fontWeight: 700 }}>
            {'> '}
            {troubleCode} · {errorMessage}
          </Box>
          {componentStack ? <Box sx={{ color: alpha(INK, 0.65) }}>{componentStack.trim()}</Box> : null}
        </Box>
      </Collapse>
    </Box>
  );
};

export const ErrorFallback = ({ error, componentStack }: PostHogErrorBoundaryFallbackProps) => {
  const troubleCode = getTroubleCode(error);
  const errorMessage = getErrorMessage(error);

  return (
    <ThemeProvider theme={lightTheme}>
      <Box sx={styles.root}>
        <Box sx={styles.container}>
          <Box sx={[styles.topBar, riseIn(0)]}>
            <Logo />
            <Stack direction="row" spacing={1} sx={styles.faultBadge}>
              <Box sx={styles.dot} />
              <Box component="span">Fault detected</Box>
            </Stack>
          </Box>

          <Box sx={styles.main}>
            <Box sx={[styles.codeLabel, riseIn(120)]}>
              <Box component="span">Diagnostic Code</Box>
              <Box sx={styles.codeDivider} />
              <Box component="span">{new Date().toLocaleString()}</Box>
            </Box>

            <Box sx={[styles.codeRow, riseIn(220)]}>
              <Typography component="div" sx={styles.codeText}>
                {troubleCode}
              </Typography>
              <Gauge />
            </Box>

            <Typography component="h1" sx={[styles.heading, riseIn(320)]}>
              A wrench in the works.
            </Typography>

            <Typography sx={[styles.body, riseIn(400)]}>
              Something threw the dashboard into limp mode. The issue's been logged for our mechanics to investigate.
              Try reloading — if the light stays on, head back to the garage and we'll take another look.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={[{ mb: 6 }, riseIn(480)]}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleReload}
                startIcon={<RefreshRoundedIcon />}
                sx={styles.primaryBtn}
              >
                Reload dashboard
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                onClick={handleHome}
                startIcon={<HomeRoundedIcon />}
                sx={styles.secondaryBtn}
              >
                Return home
              </Button>
            </Stack>

            {import.meta.env.DEV ? (
              <TechnicalDetails troubleCode={troubleCode} errorMessage={errorMessage} componentStack={componentStack} />
            ) : null}
          </Box>

          <Box sx={[styles.footer, riseIn(640)]}>
            <Box component="span">TorqueTitan · Diagnostic System</Box>
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Signal
              <Box component="span" sx={{ animation: `${blink} 1.1s steps(2, start) infinite` }}>
                _
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};
